import { onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import { ButtonV2 } from "@opencode-ai/ui/v2/button-v2"
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle } from "@opencode-ai/ui/v2/dialog-v2"
import { useLanguage } from "@/context/language"
import type { DatoxArtifact } from "@/datox/api"
import { reportDisplayTitle } from "@/datox/report-preview"
import "@/datox/report-actions.css"

export function DatoxReportDeleteButton(props: { artifact: DatoxArtifact; onDelete: (artifact: DatoxArtifact) => Promise<void> }) {
  const language = useLanguage()
  const dialog = useDialog()
  const [store, setStore] = createStore({ busy: false, error: false })
  const open = () => {
    const artifact = props.artifact
    setStore("error", false)
    dialog.show(() => {
      let mounted = true
      onCleanup(() => { mounted = false })
      const confirm = async () => {
        if (store.busy) return
        setStore({ busy: true, error: false })
        try {
          await props.onDelete(artifact)
          if (mounted) dialog.close()
        } catch {
          if (mounted) setStore("error", true)
        } finally {
          setStore("busy", false)
        }
      }
      return <Dialog fit>
        <DialogHeader hideClose={store.busy}><DialogTitle>{language.t("datox.pipeline.report.deleteTitle")}</DialogTitle></DialogHeader>
        <DialogBody class="w-96 max-w-full px-4 py-4 text-14-regular">
          <p class="break-words">{language.t("datox.pipeline.report.deleteConfirm", { title: reportDisplayTitle(artifact.title) })}</p>
          <p class="mt-2 text-12-regular text-v2-text-text-muted">{language.t("datox.pipeline.report.deleteDescription")}</p>
          <Show when={store.error}><p role="alert" class="mt-3 text-12-regular">{language.t("datox.pipeline.report.deleteError")}</p></Show>
        </DialogBody>
        <DialogFooter>
          <ButtonV2 variant="neutral" autofocus disabled={store.busy} onClick={() => dialog.close()}>{language.t("common.cancel")}</ButtonV2>
          <button type="button" data-datox-report-delete disabled={store.busy} aria-busy={store.busy} onClick={() => void confirm()}>{language.t(store.busy ? "datox.pipeline.report.deleting" : "datox.pipeline.report.delete")}</button>
        </DialogFooter>
      </Dialog>
    })
  }
  return <button type="button" data-datox-report-delete disabled={store.busy} onClick={open}>{language.t("datox.pipeline.report.delete")}</button>
}
